import React, { useEffect, useState } from "react";
import useAxios from "../../../hooks/useAxios";
import "./StudentDashboardPage.css";
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Button,
  useMediaQuery,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Components
import Header from "../../../components/Header/Header";
import ContentHeader from "../../../components/common/ContentHeader/ContentHeader";
import StudentAddClassModal from "../../../components/DashboardComponents/Modals/StudentAddClassModal/StudentAddClassModal";
import ActivateAccountAlert from "../../../components/Auth/ActivateAccountAlert/ActivateAccountAlert";
import StudentReportTable from "../../../components/DashboardComponents/StudentDashboard/StudentReportTable/StudentReportTable";
import ClassCard from "../../../components/QuizComponents/Cards/ClassCard/ClassCard";
import { useNavigate } from "react-router-dom";

// Navigation Links
const navLinks = [];

function StudentDashboardPage() {
  const navigate = useNavigate();
  const [classesList, setClassesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const axiosInstance = useAxios();
  const [profile, setProfile] = useState(null);
  const [isActivateAlertOpen, setIsActivateAlertOpen] = useState(false);
  const [isAddClassModalOpen, setIsAddClassModalOpen] = useState(false);
  const [reportData, setReportData] = useState([]); // For your StudentReportTable

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Determine how many items to show initially
  let initialDisplayCount = 3;
  if (isSmallScreen) {
    initialDisplayCount = 1;
  } else if (isMediumScreen) {
    initialDisplayCount = 2;
  }

  const [showAllClasses, setShowAllClasses] = useState(false);

  // Fetch classes from API
  const fetchClasses = async () => {
    try {
      const response = await axiosInstance.get("/classes/student-classes");
      if (response.data.success) {
        const classes = response.data.classes;

        // For each class, fetch the filtered quiz data using the same endpoint as StudentClassPage
        const classesWithFilteredQuizzes = await Promise.all(
          classes.map(async (classData) => {
            try {
              const quizResponse = await axiosInstance.get(
                `/classes/student/${classData.id}`
              );
              if (quizResponse.data.success) {
                const { class: classWithQuizzes } = quizResponse.data;
                console.log("Class with quizzes:", classWithQuizzes);
                return {
                  ...classData,
                  quizzes: Array.isArray(classWithQuizzes.quizzes)
                    ? classWithQuizzes.quizzes
                    : [],
                  quizzes_count: Array.isArray(classWithQuizzes.quizzes)
                    ? classWithQuizzes.quizzes.length
                    : 0,
                };
              }
              return classData;
            } catch (err) {
              console.error(
                `Error fetching quizzes for class ${classData.id}:`,
                err
              );
              return classData;
            }
          })
        );

        setClassesList(classesWithFilteredQuizzes);
      } else {
        setError(response.data.message || "Failed to fetch classes.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  // Called when the Add Class button is clicked (optional)
  const handleAddClass = () => {
    setIsAddClassModalOpen(true);
  };

  const handleClose = () => {
    setIsAddClassModalOpen(false);
    fetchClasses();
  };

  // Determine which classes to show
  const displayedClasses = showAllClasses
    ? classesList
    : classesList.slice(0, initialDisplayCount);

  const totalClasses = classesList.length;
  const isSubset = !showAllClasses && totalClasses > displayedClasses.length;

  // Handle what to display in the UI
  let content;
  if (loading) {
    content = (
      <Box className="loading-container">
        <CircularProgress />
        <Typography variant="h6" className="loading-text">
          Fetching Classes...
        </Typography>
      </Box>
    );
  } else if (error) {
    content = (
      <Box className="error-container">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  } else {
    // We have classes
    content = (
      <>
        {/* Page Title & Action Button */}
        <ContentHeader
          title="Your Classes"
          buttonLabel="Add Class"
          onButtonClick={handleAddClass}
        />

        {/* Classes Grid */}
        <Box sx={{ flexGrow: 1, marginTop: 2 }}>
          <Grid container spacing={3}>
            {displayedClasses.length ? (
              displayedClasses.map((classData) => (
                <Grid item xs={12} sm={6} md={4} key={classData.id}>
                  <ClassCard
                    classData={classData}
                    onClick={() => {
                      navigate(`/student/class/${classData.id}`);
                    }}
                  />
                </Grid>
              ))
            ) : (
              <Typography>No classes to show</Typography>
            )}
          </Grid>
        </Box>

        {/* Show More / Show Less Button */}
        {(isSubset ||
          (showAllClasses && totalClasses > initialDisplayCount)) && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={2}
          >
            <Typography variant="body2" sx={{ mr: 2 }}>
              {showAllClasses
                ? `Showing all ${totalClasses} classes`
                : `Showing ${displayedClasses.length} of ${totalClasses} classes`}
            </Typography>
            <Button
              className="text-button"
              onClick={() => setShowAllClasses(!showAllClasses)}
            >
              {showAllClasses ? "View Less" : "View All"}
            </Button>
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      <Header navLinks={navLinks} isScrollEffect={false} />
      <Container className="owner-quizzes-container page-container">
        {content}
        <ActivateAccountAlert
          open={isActivateAlertOpen}
          onClose={() => setIsActivateAlertOpen(false)}
        />
      </Container>
      <StudentAddClassModal open={isAddClassModalOpen} onClose={handleClose} />
      {/* <StudentReportTable data={reportData} /> */}
    </>
  );
}

export default StudentDashboardPage;
